import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, concatMap, map, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginRequest } from '../models/user';
import { StaticData } from '../models/static-data.model';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { BackendService, STATIC_STORAGE_KEY } from './backend.service';

type LoginResponseBody = { user?: Record<string, string>; idtoken?: string };

function normalizeLoginResponse(resp: unknown): LoginResponseBody {
  if (resp == null) return {};
  const obj = resp as Record<string, unknown>;
  const body = obj['body'];
  if (body && typeof body === 'object') return body as LoginResponseBody;
  return obj as LoginResponseBody;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  currentUserName$!: BehaviorSubject<string>;
  private sessionWatchdogInterval?: ReturnType<typeof setInterval>;

  constructor(
    private http: HttpClient,
    private backend: BackendService,
    private router: Router,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService
  ) {
    this.currentUserName$ = new BehaviorSubject<string>(sessionStorage.getItem('current-username') ?? '');
    if (this.currentUserName$.value && !this.isBackendTokenExpired()) {
      this.startSessionWatchdog();
    }
  }

  loginSso() {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  login(loginData: LoginRequest) {
    return this.http.post<unknown>(environment.LOGIN_SSO_URL, loginData).pipe(
      map(normalizeLoginResponse),
      tap((response) => {
        const user = response?.user;
        const username = user?.['USERNAME'] ?? '';
        const email = user?.['EMAIL'] ?? '';
        this.currentUserName$.next(username);
        sessionStorage.setItem('current-username', username);
        if (email) sessionStorage.setItem('current-email', email);
        if (user?.['ROLE']) sessionStorage.setItem('current-role', user['ROLE']);
        sessionStorage.setItem('id-token', response?.idtoken ?? '');
      }),
      concatMap(() => this.backend.getStatic()),
      tap((staticData) => {
        sessionStorage.setItem(STATIC_STORAGE_KEY, JSON.stringify(staticData));
        this.startSessionWatchdog();
      })
    );
  }

  getStaticData(): StaticData | null {
    const raw = sessionStorage.getItem(STATIC_STORAGE_KEY);
    if (raw == null) return null;
    try { return JSON.parse(raw) as StaticData; } catch { return null; }
  }

  logout() {
    this.stopSessionWatchdog();
    sessionStorage.clear();
    this.currentUserName$.next('');
  }

  isBackendTokenExpired(): boolean {
    try {
      const idToken = sessionStorage.getItem('id-token');
      if (!idToken) return true;
      const payload = JSON.parse(atob(idToken.split('.')[1])) as { exp?: number };
      if (!payload.exp) return true;
      return payload.exp < Math.floor(Date.now() / 1000);
    } catch { return true; }
  }

  isMsalLoggedIn(): boolean {
    const token = sessionStorage.getItem('msal-access-token');
    if (!token) return false;
    try {
      const data = JSON.parse(token) as { expiry?: string };
      return data.expiry ? new Date(data.expiry).getTime() > Date.now() : false;
    } catch { return false; }
  }

  private startSessionWatchdog(): void {
    this.stopSessionWatchdog();
    this.sessionWatchdogInterval = setInterval(() => {
      if (this.isBackendTokenExpired()) {
        this.logout();
        this.router.navigate(['/login'], { queryParams: { expired: true } });
      }
    }, 60_000);
  }

  private stopSessionWatchdog(): void {
    if (this.sessionWatchdogInterval !== undefined) {
      clearInterval(this.sessionWatchdogInterval);
      this.sessionWatchdogInterval = undefined;
    }
  }
}
